import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useCustomer, formatApiErrorDetail } from "@/context/CustomerContext";
import { Input } from "@/components/ui/input";
import { Phone, Lock, LogIn } from "lucide-react";
import { toast } from "sonner";

export default function CustomerLogin() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/account";
  const { login } = useCustomer();
  const [form, setForm] = useState({ mobile: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(form.mobile, form.password);
      toast.success("Welcome back!");
      navigate(next);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-card border border-stone-200 rounded-3xl p-7 shadow-sm">
        <h1 className="font-display font-extrabold text-3xl tracking-tight" data-testid="login-title">
          Welcome back
        </h1>
        <p className="text-muted-foreground text-sm mt-1 mb-6">
          Log in to view your bookings and saved favourites.
        </p>
        <form onSubmit={submit} className="grid gap-3">
          <label className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="login-mobile"
              className="pl-9"
              placeholder="Mobile number"
              inputMode="numeric"
              value={form.mobile}
              onChange={(e) => setForm({ ...form, mobile: e.target.value })}
            />
          </label>
          <label className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="login-password"
              className="pl-9"
              type="password"
              placeholder="Password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
          </label>
          {error && (
            <p data-testid="login-error" className="text-sm text-red-600 font-medium">
              {error}
            </p>
          )}
          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="mt-2 h-12 bg-primary text-white rounded-full font-semibold flex items-center justify-center gap-2 disabled:opacity-60 hover:bg-primary/90 transition-colors"
          >
            <LogIn className="h-5 w-5" /> {loading ? "Logging in..." : "Log in"}
          </button>
        </form>
        <p className="text-sm text-center text-muted-foreground mt-5">
          New here?{" "}
          <Link
            to={`/register${next !== "/account" ? `?next=${encodeURIComponent(next)}` : ""}`}
            data-testid="login-to-register"
            className="text-primary font-semibold"
          >
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
}
